import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { DeviceInfo } from '../types/auth';
import { copyToClipboard } from '../utils/clipboardService';

/**
 * Best-effort human-readable browser + OS label from the user agent. Not
 * exhaustive — just enough for the user to recognize which device this is.
 */
function describeBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown browser';
  const ua = navigator.userAgent;

  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';

  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/(iPhone|iPad|iPod)/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return os ? `${browser} on ${os}` : browser;
}

interface MyDeviceProps {
  device?: DeviceInfo | null;
  /** Link text; defaults to "this device" so it reads inline in a sentence. */
  label?: string;
}

/**
 * Inline "this device" link that opens a modal with the current device's
 * details (IP, location, browser, timestamp). Designed to sit mid-sentence:
 *   We recognize your account but not <MyDevice/>.
 */
const MyDevice: React.FC<MyDeviceProps> = ({
  device,
  label = 'this device',
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const rows: { field: string; value: string }[] = [
    { field: 'IP address', value: device?.ip || 'Unknown' },
    { field: 'Location', value: device?.geoLocation || 'Unknown' },
    { field: 'Site host', value: device?.rpId || 'Unknown' },
    { field: 'Browser', value: describeBrowser() },
    { field: 'Checked', value: new Date().toLocaleString() },
  ];

  const handleCopy = async (field: string, value: string) => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(field);
      window.setTimeout(() => setCopied(null), 1500);
    }
  };

  const copyAll = () => {
    const text = rows.map((r) => `${r.field}: ${r.value}`).join('\n');
    handleCopy('all', text);
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        underline="always"
        sx={{ verticalAlign: 'baseline', font: 'inherit' }}
      >
        {label}
      </Link>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Device details</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Table size="small">
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.field}>
                  <TableCell
                    sx={{ fontWeight: 600, width: 110, verticalAlign: 'top' }}
                  >
                    {r.field}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {r.value}
                  </TableCell>
                  <TableCell sx={{ width: 40, p: 0.5 }}>
                    <Tooltip
                      title={copied === r.field ? 'Copied' : 'Copy'}
                      placement="left"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(r.field, r.value)}
                        aria-label={`Copy ${r.field}`}
                      >
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Share your IP with an admin so they can approve this device.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={copyAll}>
            {copied === 'all' ? 'Copied' : 'Copy all'}
          </Button>
          <Button onClick={() => setOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyDevice;
